// Admin add-website form (behind the shared-secret gate). Creates a client (by
// name), website, config and default TOV profile, then navigates to the new
// website's dashboard where a scan can be started.
import { useNavigate } from "@solidjs/router";
import {
  DEFAULT_SCRAPE_COUNTRY_CODE,
  type ScrapeCountryCode,
} from "contracts/enums/scrape-country";
import { TOV_SOURCE_TYPE } from "contracts/enums/tov";
import { createSignal, Show } from "solid-js";
import type { GuidelinesChoice } from "../../enums/admin";
import { GUIDELINES_CHOICE } from "../../enums/admin";
import { api } from "../../store/api";
import { createAsyncAction } from "../../ui/asyncAction";
import {
  COUNTRY_FIELD_LABEL,
  COUNTRY_OPTIONS,
} from "../website/countryOptions";
import { SelectField } from "./editorFields";

export function AddWebsitePanel(props: { onClose: () => void }) {
  const navigate = useNavigate();
  const [clientName, setClientName] = createSignal("");
  const [originUrl, setOriginUrl] = createSignal("");
  const [displayName, setDisplayName] = createSignal("");
  const [maxPages, setMaxPages] = createSignal(50);
  const [maxDepth, setMaxDepth] = createSignal(1);
  const [countryCode, setCountryCode] = createSignal<ScrapeCountryCode>(
    DEFAULT_SCRAPE_COUNTRY_CODE,
  );
  const [guidelines, setGuidelines] = createSignal<GuidelinesChoice>(
    GUIDELINES_CHOICE.DEFAULT,
  );
  const [guidelinesFile, setGuidelinesFile] = createSignal<File | null>(null);
  const action = createAsyncAction((cause) =>
    cause instanceof Error ? cause.message : "Create failed",
  );

  const submit = (event: SubmitEvent) => {
    event.preventDefault();
    const ownFile = guidelinesFile();
    if (guidelines() === GUIDELINES_CHOICE.UPLOAD && !ownFile) {
      action.fail("Choose a brand guidelines file to upload");
      return;
    }
    void action.run(async () => {
      const created = await api.createWebsite({
        clientName: clientName().trim(),
        originUrl: originUrl().trim(),
        config: {
          maxPages: maxPages(),
          maxDepth: maxDepth(),
          countryCode: countryCode(),
        },
        useDefaultGuidelines: guidelines() === GUIDELINES_CHOICE.DEFAULT,
        ...(displayName().trim() ? { displayName: displayName().trim() } : {}),
      });
      if (guidelines() === GUIDELINES_CHOICE.UPLOAD && ownFile) {
        await api.tov.uploadSource(
          created.clientId,
          created.tovVersionId,
          ownFile,
          { sourceType: TOV_SOURCE_TYPE.PDF, title: ownFile.name },
        );
      }
      props.onClose();
      navigate(`/w/${created.websiteId}`);
    });
  };

  return (
    <div class="scrim scrim-right">
      <aside class="drawer">
        <div class="drawer-head">
          <h3 class="drawer-title">Add website</h3>
          <button
            class="icon-btn"
            type="button"
            aria-label="Close"
            onClick={props.onClose}
          >
            ✕
          </button>
        </div>
        <form class="drawer-body editor" onSubmit={submit}>
          <label class="editor-field">
            <span class="editor-field-label">Organisation name</span>
            <input
              class="editor-input"
              type="text"
              required
              value={clientName()}
              onInput={(event) => setClientName(event.currentTarget.value)}
            />
          </label>
          <label class="editor-field">
            <span class="editor-field-label">Website URL</span>
            <input
              class="editor-input"
              type="url"
              required
              placeholder="https://example.com"
              value={originUrl()}
              onInput={(event) => setOriginUrl(event.currentTarget.value)}
            />
          </label>
          <label class="editor-field">
            <span class="editor-field-label">Website name (optional)</span>
            <input
              class="editor-input"
              type="text"
              value={displayName()}
              onInput={(event) => setDisplayName(event.currentTarget.value)}
            />
          </label>
          <SelectField
            label={COUNTRY_FIELD_LABEL}
            value={countryCode()}
            options={COUNTRY_OPTIONS}
            onInput={setCountryCode}
          />
          <fieldset class="editor-field">
            <legend class="editor-field-label">Guidance for this scan</legend>
            <label class="radio-row">
              <input
                type="radio"
                name="guidelines"
                checked={guidelines() === GUIDELINES_CHOICE.DEFAULT}
                onChange={() => setGuidelines("default")}
              />
              <span>Use Faro baseline guidance</span>
            </label>
            <label class="radio-row">
              <input
                type="radio"
                name="guidelines"
                checked={guidelines() === GUIDELINES_CHOICE.UPLOAD}
                onChange={() => setGuidelines("upload")}
              />
              <span>Add brand guidelines</span>
            </label>
            <Show when={guidelines() === GUIDELINES_CHOICE.UPLOAD}>
              <input
                class="editor-input"
                type="file"
                accept="application/pdf"
                onChange={(event) =>
                  setGuidelinesFile(event.currentTarget.files?.[0] ?? null)
                }
              />
            </Show>
          </fieldset>
          <details class="editor-group">
            <summary>Advanced scan settings</summary>
            <label class="editor-field">
              <span class="editor-field-label">Page limit</span>
              <input
                class="editor-input mono"
                type="number"
                min="1"
                value={maxPages()}
                onInput={(event) =>
                  setMaxPages(
                    Number.parseInt(event.currentTarget.value, 10) || 1,
                  )
                }
              />
            </label>
            <label class="editor-field">
              <span class="editor-field-label">Link depth</span>
              <input
                class="editor-input mono"
                type="number"
                min="0"
                value={maxDepth()}
                onInput={(event) =>
                  setMaxDepth(
                    Number.parseInt(event.currentTarget.value, 10) || 0,
                  )
                }
              />
            </label>
          </details>
          <div class="editor-foot">
            <Show when={action.error()}>
              <span class="gate-error">{action.error()}</span>
            </Show>
            <button
              class="btn btn-primary"
              type="submit"
              disabled={action.busy()}
            >
              {action.busy() ? "Adding website..." : "Add website"}
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}
